import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle blocked user errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
      // User is blocked - logout automatically
      const errorMessage = error.response.data?.error || '';
      if (errorMessage.includes('заблокирован') || errorMessage.includes('blocked')) {
        sessionStorage.removeItem('token');
        window.location.href = '/login';
        alert('Ваш аккаунт заблокирован. Обратитесь к администратору.');
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (name, email, password) =>
    api.post('/auth/register', { name, email, password }),
  login: (email, password, twoFactorToken = '') =>
    api.post('/auth/login', { email, password, twoFactorToken }),
  getCurrentUser: () =>
    api.get('/auth/me'),
};

export const topicAPI = {
  getAllTopics: (params = {}) => {
    // Backwards-compatible: allow passing language string or params object
    if (typeof params === 'string') {
      return api.get('/topics', { params: { language: params } });
    }
    return api.get('/topics', { params });
  },
  getTopic: (id) =>
    api.get(`/topics/${id}`),
  createTopic: (data) =>
    api.post('/topics', data),
  updateTopic: (id, data) =>
    api.put(`/topics/${id}`, data),
  deleteTopic: (id) =>
    api.delete(`/topics/${id}`),
  reorderTopic: (id, direction) =>
    api.put(`/topics/${id}/reorder`, { direction }),
  normalizeOrders: () =>
    api.post('/topics/normalize-orders'),
};

export const exerciseAPI = {
  getExercises: (topicId) =>
    api.get(`/exercises/topic/${topicId}`),
  getExercise: (id) =>
    api.get(`/exercises/${id}`),
  createExercise: (data) =>
    api.post('/exercises', data),
  updateExercise: (id, data) =>
    api.put(`/exercises/${id}`, data),
  deleteExercise: (id) =>
    api.delete(`/exercises/${id}`),
};

export const progressAPI = {
  getProgress: (topicId) =>
    api.get(`/progress/topic/${topicId}`),
  getAllProgress: () =>
    api.get('/progress'),
  updateProgress: (data) =>
    api.post('/progress/update', data),
  setManualCompletion: (topicId, completed) =>
    api.post('/progress/manual', { topicId, completed }),
};

export const adminAPI = {
  getStats: () =>
    api.get('/admin/stats'),
  getUsers: (params = {}) =>
    api.get('/admin/users', { params }),
  getUserProgress: (userId) =>
    api.get(`/admin/users/${userId}/progress`),
  blockUser: (userId) =>
    api.put(`/admin/users/${userId}/block`),
  unblockUser: (userId) =>
    api.put(`/admin/users/${userId}/unblock`),
  setTelegramAccess: (userId, enabled) =>
    api.put(`/admin/users/${userId}/telegram-access`, { enabled }),
  deleteUser: (userId) =>
    api.delete(`/admin/users/${userId}`),
  createStudent: (data) =>
    api.post('/admin/students', data),
  // Teacher management (admin only)
  getTeachers: () =>
    api.get('/admin/teachers'),
  createTeacher: (data) =>
    api.post('/admin/teachers', data),
  blockTeacher: (teacherId) =>
    api.put(`/admin/teachers/${teacherId}/block`),
  unblockTeacher: (teacherId) =>
    api.put(`/admin/teachers/${teacherId}/unblock`),
  deleteTeacher: (teacherId) =>
    api.delete(`/admin/teachers/${teacherId}`),
};

export const telegramAPI = {
  getStatus: () =>
    api.get('/telegram/status'),
  generateLinkCode: () =>
    api.post('/telegram/link-code')
};

export const ticketAPI = {
  createTicket: (data) =>
    api.post('/tickets', data),
  getTickets: () =>
    api.get('/tickets'),
  getTicketSummary: () =>
    api.get('/tickets/summary'),
  updateTicketStatus: (ticketId, status) =>
    api.put(`/tickets/${ticketId}/status`, { status }),
  deleteTicket: (ticketId) =>
    api.delete(`/tickets/${ticketId}`)
};

export default api;
